import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Button,
  Divider,
  IconButton,
  Autocomplete,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  Box,
  Tooltip,
  Chip,
  InputLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { getProject } from "../../redux/slices/projectSlice/addProjectSlice";
import { RootState, AppDispatch } from "../../redux/store/appStore";
import { STATUS } from "../../utils/status";
import debounce from "lodash.debounce";
import api from "../../utils/api";
import { useParams } from "react-router-dom";
import { getReview } from "../../redux/slices/reviewerSlice";

interface ProjectOption {
  project_id: string;
  project_name: string;
}

const ProjectClonePopup = ({ open, setProjectClonePopupOpen, onSubmit, clonePopUpTitle, }: any) => {
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sourceStages, setSourceStages] = useState<any[]>([]);
  const [destStages, setDestStages] = useState<any[]>([]);
  const [options, setOptions] = useState<ProjectOption[]>([]);
  const [mappings, setMappings] = useState<{ source: string; destination: string }[]>([{ source: "", destination: "" }]);
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const getReviewStages = useSelector((state: RootState) => state.reviewStage.reviewer);
  const getReviewStatus = useSelector((state: RootState) => state.reviewStage.getReviewStatus);

  const path = window.location.href.includes('review')

  const handleProjectCloneClose = () => {
      setSelectedProject(null);
      setInputValue("");
      setError("");
      setSourceStages([]);
      setDestStages([]);
      setMappings([{ source: "", destination: "" }]);
    setProjectClonePopupOpen(false)
  };

  useEffect(() => {
    if (id) dispatch(getReview({ project_id: id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (getReviewStatus === STATUS.SUCESS && getReviewStages?.results) {
      setSourceStages(getReviewStages.results);
    }
  }, [getReviewStatus, getReviewStages]);

  useEffect(() => {
    const controller = new AbortController();
    const fetchOptions = async () => {

      setLoading(true);
      try {
        const response = await api.get<{ results: any[] }>("project/user", {
          params: {
            paginate: false,
            search: inputValue || "",
          },
          signal: controller.signal,
        });
        const mappedOptions: ProjectOption[] = response.data.results.map(
          (item: any) => ({
            project_id: item.project_id,
            project_name: item.project_name,
          })
        );
        const uniqueOptions = Array.from(
          new Map(mappedOptions.map((item) => [item.project_id, item])).values()
        );
        setOptions(uniqueOptions);
      } catch (error) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    const delayDebounce = setTimeout(() => {
      fetchOptions();
    }, 300); // Debounce input

    return () => {
      clearTimeout(delayDebounce);
      controller.abort();
    };
  }, [inputValue]);

  const handleProjectChange = async (event: any, newValue: any) => {
    console.log({ newValue });
    setSelectedProject(newValue);
    if (!newValue) {
      setSourceStages([]);
      return;
    }

    if (newValue?.project_id) {
      try {
        const queryString = new URLSearchParams({
          ...(newValue?.project_id && { project_id: String(newValue.project_id) }),
          paginate: "false",
        }).toString();
        const resp = await api.get(`project/company/stage/?${queryString}`);
        console.log({ resp });
        setDestStages(resp?.data || []);
      } catch (err) {
        setDestStages([]);
      }
    }
  };

  const handleMappingChange = (index: number, field: 'source' | 'destination', value: string) => {
    const updated = [...mappings];
    updated[index][field] = value

    const sourceStage = sourceStages.find((s) => s.id === (field === "source" ? value : updated[index].source));
    const destStage = destStages.find((s) => s.id === (field === "destination" ? value : updated[index].destination));

    if (sourceStage && destStage && sourceStage.category !== destStage.category) {
      setError(`Invalid mapping: "${sourceStage.category}" cannot be mapped with "${destStage.category}".`);
      return;
    } else {
      setError("");
    }

    setMappings(updated);
  };


  const handleAddMapping = () => setMappings([...mappings, { source: "", destination: "" }]);
  const handleRemoveMapping = (index: number) => setMappings(mappings.filter((_, i) => i !== index));

  const handleSubmit = () => {
    if (!selectedProject) return setError("Please select a project to clone.");
    const validMappings = mappings.filter(m => m.source && m.destination);
    if (validMappings.length === 0 && !path) return setError("Please map at least one stage pair.");
    onSubmit({
      target_project_id: id,
      source_project_id: selectedProject.project_id,
      ...(!path && {
        stage_mappings: validMappings.map(m => ({
          source_stage_id: m.source,
          target_stage_id: m.destination,
        })),
      }),
    });
    handleProjectCloneClose();
  };


  return (
    <Dialog open={open} onClose={() => { setError(""); handleProjectCloneClose(); }} keepMounted PaperProps={{ sx: { display: "flex", overflow: "hidden", height: "44vh", width: "92vh" } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "16px", fontWeight: "bold", padding: "16px 24px", fontFamily: "montserrat" }}>
        Copy {clonePopUpTitle} from Existing Project
        <IconButton onClick={handleProjectCloneClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ flex: "1", pb: 0 }}>
        <FormControl fullWidth>
          <Autocomplete
            value={selectedProject}
            onChange={handleProjectChange}
            inputValue={inputValue}
            onInputChange={(e, newInput) => setInputValue(newInput)}
            options={options}
            getOptionLabel={(option) => option.project_name}
            isOptionEqualToValue={(option, value) => option?.project_id === value?.project_id}
            filterOptions={(x) => x}
            loading={loading}
            renderOption={(props, option) => (<li {...props} key={option.project_id}>{option.project_name}</li>)}
            renderInput={(params) => (
              <TextField {...params} label="Select Project" InputProps={{ ...params.InputProps, endAdornment: (<>{loading ? <CircularProgress size={20} /> : null}{params.InputProps.endAdornment}</>) }} />
            )}
          />

          {!path && <div style={{ marginTop: "20px" }}>
            <div style={{ fontWeight: 600, marginBottom: "10px" }}>Stage Mapping</div>

            {mappings.map((mapping, index) => (
              <div key={index} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                <TextField
                  select
                  id="demo-simple-select"
                  label="Source Stage"
                  value={mapping.source}
                  onChange={(e) => handleMappingChange(index, "source", e.target.value)}
                  // SelectProps={{ native: true }}
                  fullWidth
                  size="small"
                >
                  {destStages
                    .filter((stage: any) => {
                      const selectedDest = sourceStages.find((s) => s.id === mapping.destination);
                      if (!selectedDest) return true;
                      return stage.category === selectedDest.category;
                    }).map((stage: any) => (
                      <MenuItem key={stage.id} value={stage.id}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            minWidth: "150px",
                            maxWidth: "210px",
                          }}

                        >
                          <Tooltip title={stage.name} arrow>
                            <span
                              style={{
                                fontFamily: "montserrat",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                maxWidth: "150px",
                                display: "inline-block",
                              }}
                            >
                              {stage.name}
                            </span>
                          </Tooltip>

                          <Chip
                            size="small"
                            sx={{
                              fontSize: "11px",
                              backgroundColor: stage.category === "REVIEW" ? "#1976d2" : "default",
                              color: stage.category === "REVIEW" ? "#ffffff" : "#000000",
                              border: "1px solid #ccc",
                              marginLeft: "10px",
                              flexShrink: 0,
                            }}
                            label={stage?.category}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                </TextField>

                <span style={{ fontSize: "18px" }}>→</span>

                <TextField
                  select
                  label="Destination Stage"
                  value={mapping.destination}
                  onChange={(e) => handleMappingChange(index, "destination", e.target.value)}
                  // SelectProps={{ native: true }}
                  fullWidth
                  size="small"
                >
                  {sourceStages
                    .filter((stage: any) => {
                      const selectedSource = destStages.find((s) => s.id === mapping.source);
                      if (!selectedSource) return true;
                      return stage.category === selectedSource.category;
                    }).map((stage: any) => (
                      <MenuItem key={stage.id} value={stage.id}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            minWidth: "150px",
                            maxWidth: "210px",
                          }}

                        >
                          <Tooltip title={stage.name} arrow>
                            <span
                              style={{
                                fontFamily: "montserrat",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                maxWidth: "150px",
                                display: "inline-block",
                              }}
                            >
                              {stage.name}
                            </span>
                          </Tooltip>

                          <Chip
                            size="small"
                            sx={{
                              fontSize: "11px",
                              backgroundColor: stage.category === "REVIEW" ? "#1976d2" : "default",
                              color: stage.category === "REVIEW" ? "#ffffff" : "#000000",
                              border: "1px solid #ccc",
                              marginLeft: "10px",
                              flexShrink: 0,
                            }}
                            label={stage.category || "Task"}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                </TextField>

                {index === mappings.length - 1 && mappings.length < 5 ? (
                  <IconButton onClick={handleAddMapping} color="primary"><span style={{ fontSize: "20px" }}>+</span></IconButton>
                ) : (
                  <IconButton onClick={() => handleRemoveMapping(index)} color="error"><span style={{ fontSize: "20px" }}>−</span></IconButton>
                )}
              </div>
            ))}
          </div>}
        </FormControl>

        <span style={{ color: "red", fontSize: "13px", paddingTop: "14px" }}>{error}</span>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "flex-end", p: 2 }}>
        <Button onClick={handleSubmit} disableElevation variant="contained" sx={{ borderRadius: "8px", padding: "8px 16px", backgroundColor: "#3D85C6", textTransform: "none", "&:hover": { backgroundColor: "#3D85C6" } }}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectClonePopup;
